import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, message, Spin, Typography, QRCode } from 'antd';
import { QrcodeOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { request } from '@umijs/max';

const { Title, Text } = Typography;

interface WechatBindingModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface BindingStatus {
  is_bound: boolean;
  wechat_info?: {
    nickname: string;
    avatar: string;
    bind_time: string;
  };
}

const WechatBindingModal: React.FC<WechatBindingModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [authUrlLoading, setAuthUrlLoading] = useState(false);
  const [bindingStatus, setBindingStatus] = useState<BindingStatus | null>(null);
  const [authUrl, setAuthUrl] = useState('');
  const hasGeneratedAuthUrlRef = useRef(false); // 防止重复生成授权URL
  /** 已展示扫码二维码，仅在此时轮询到绑定成功才提示成功并关窗，避免误判 */
  const awaitingBindAfterQrRef = useRef(false);

  // 生成授权URL
  const generateAuthUrl = useCallback(async () => {
    // 防止重复生成
    if (hasGeneratedAuthUrlRef.current) {
      return;
    }

    try {
      setAuthUrlLoading(true);
      hasGeneratedAuthUrlRef.current = true;

      const response = await request('/api/wechat-binding/auth-url', {
        method: 'GET',
      });

      let data = response;
      if (response && typeof response === 'object' && 'data' in response && response.data) {
        data = response.data;
      }

      if (data && typeof data === 'object' && data.auth_url) {
        const st = await request('/api/wechat-binding/status', { method: 'GET' });
        const sd =
          st && typeof st === 'object' && 'data' in st && st.data ? st.data : st;
        const already =
          sd?.is_bound === true || String(sd?.is_bound) === 'true';
        if (already) {
          setBindingStatus({
            is_bound: true,
            wechat_info: sd?.wechat_info,
          });
          return;
        }
        setAuthUrl(data.auth_url);
        awaitingBindAfterQrRef.current = true;
        message.info('请使用微信扫码；绑定成功后将自动提示');
      } else {
        message.error('生成授权链接失败：响应数据格式错误');
        hasGeneratedAuthUrlRef.current = false;
      }
    } catch (error: any) {
      const errorMessage = error?.message || error?.info?.errorMessage || '生成授权链接失败';
      message.error(errorMessage);
      hasGeneratedAuthUrlRef.current = false;
    } finally {
      setAuthUrlLoading(false);
    }
  }, []); // 空依赖数组，函数只创建一次

  // 检查绑定状态
  const checkBindingStatus = useCallback(async () => {
    try {
      const response = await request('/api/wechat-binding/status', {
        method: 'GET',
      });

      const data =
        response && typeof response === 'object' && 'data' in response && response.data
          ? response.data
          : response;
      const bound = data?.is_bound === true || String(data?.is_bound) === 'true';

      if (bound) {
        setBindingStatus({
          is_bound: true,
          wechat_info: data?.wechat_info,
        });
        if (awaitingBindAfterQrRef.current) {
          awaitingBindAfterQrRef.current = false;
          message.success('微信绑定成功！');
          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 2500);
        }
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [onSuccess, onClose]);

  // 解绑微信
  const unbindWechat = async () => {
    try {
      const response = await request('/api/wechat-binding/unbind', {
        method: 'POST',
      });

      if (response) {
        message.success('解绑成功');
        setBindingStatus(null);
        onSuccess?.();
      }
    } catch (error) {
      message.error('解绑失败');
    }
  };

  // 模态框打开时自动生成授权URL
  useEffect(() => {
    if (visible) {
      hasGeneratedAuthUrlRef.current = false;
      awaitingBindAfterQrRef.current = false;
      setAuthUrl('');
      generateAuthUrl();
      checkBindingStatus();
    } else {
      setAuthUrl('');
      setBindingStatus(null);
      hasGeneratedAuthUrlRef.current = false;
      awaitingBindAfterQrRef.current = false;
    }
  }, [visible, generateAuthUrl, checkBindingStatus]);

  // 轮询检查绑定状态（当二维码已生成且未绑定时）
  useEffect(() => {
    if (!visible || !authUrl || bindingStatus?.is_bound) {
      return;
    }

    // 每2秒检查一次绑定状态
    const interval = setInterval(async () => {
      const isBound = await checkBindingStatus();
      if (isBound) {
        clearInterval(interval);
      }
    }, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [visible, authUrl, bindingStatus, checkBindingStatus]);

  const renderContent = () => {
    // 如果已绑定，显示已绑定状态
    if (bindingStatus?.is_bound) {
      return (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Title level={4} style={{ color: '#52c41a' }}>微信已绑定</Title>
          {bindingStatus.wechat_info && (
            <div style={{ marginTop: 16 }}>
              <Text>微信昵称：{bindingStatus.wechat_info.nickname}</Text>
              <br />
              <Text type="secondary">绑定时间：{bindingStatus.wechat_info.bind_time}</Text>
            </div>
          )}
          <div style={{ marginTop: 24 }}>
            <Button type="primary" danger onClick={unbindWechat}>
              解绑微信
            </Button>
          </div>
        </div>
      );
    }

    // 显示授权绑定界面
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <QrcodeOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
        <Title level={4}>绑定微信账号</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          请使用微信扫描下方二维码完成授权绑定
        </Text>

        {authUrlLoading ? (
          <div style={{ padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>正在生成二维码...</div>
          </div>
        ) : authUrl ? (
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              正在等待手机端完成授权，请勿关闭此窗口…
            </Text>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#fff',
                borderRadius: 8,
                border: '1px solid #d9d9d9'
              }}>
                <QRCode
                  value={authUrl}
                  size={200}
                  errorLevel="M"
                />
              </div>
            </div>
          </div>
        ) : (
          <Button
            type="primary"
            icon={<QrcodeOutlined />}
            onClick={generateAuthUrl}
            loading={authUrlLoading}
            size="large"
          >
            生成二维码
          </Button>
        )}
      </div>
    );
  };

  return (
    <Modal
      title="微信账号绑定"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={400}
      centered
    >
      {renderContent()}
    </Modal>
  );
};

export default WechatBindingModal;
